import Image from 'next/image'
import { Badge } from '@ui/base/badge'
import { StoreIcon } from 'lucide-react'

import { type Api } from '../api/types'

/**
 * Значок площадки: кружок фирменного цвета с сокращением названия.
 * Цвета взяты у самих площадок, поэтому заданы прямо здесь, а не токенами оформления сайта.
 */
const marketplaceMarks: Record<Api.Marketplace, { short: string; className: string }> = {
  wildberries: { short: 'WB', className: 'bg-[#7B2FF7] text-white' },
  ozon: { short: 'OZ', className: 'bg-[#005BFF] text-white' },
  yandex_market: { short: 'ЯМ', className: 'bg-[#FC3F1D] text-white' },
  magnit_market: { short: 'ММ', className: 'bg-[#E5001A] text-white' },
  other: { short: '', className: 'bg-muted text-muted-foreground' },
}

/**
 * Логотип площадки. Широкий уже содержит название и заменяет подпись целиком,
 * квадратный значок ставится перед подписью. Ширина посчитана от высоты 14 px по пропорциям файла.
 */
const marketplaceLogos: Partial<Record<Api.Marketplace, { src: string; width: number; wide?: true }>> = {
  ozon: { src: '/front/images/marketplace-logo/ozon-logo.png', width: 64, wide: true },
  wildberries: { src: '/front/images/marketplace-logo/wildberries-icon.webp', width: 14 },
  yandex_market: { src: '/front/images/marketplace-logo/yandexmarket-icon.png', width: 14 },
}

const logoHeight = 14

export const MarketplaceTag = ({ marketplace, label }: { marketplace: Api.Marketplace; label: string }) => {
  const logo = marketplaceLogos[marketplace]
  if (logo?.wide) {
    return (
      <Badge variant="info" className="py-1">
        <Image src={logo.src} alt={label} width={logo.width} height={logoHeight} className="h-3.5 w-auto" />
      </Badge>
    )
  }
  if (logo) {
    return (
      <Badge variant="info" className="gap-1.5 py-1 pl-1">
        <Image
          src={logo.src}
          alt=""
          width={logo.width}
          height={logoHeight}
          className="size-4 rounded-[3px] object-contain"
        />
        {label}
      </Badge>
    )
  }

  const mark = marketplaceMarks[marketplace]

  return (
    <Badge variant="info" className="gap-1.5 pl-1">
      {/* Значок скрыт от чтения с экрана: рядом уже стоит название площадки. */}
      <span
        aria-hidden="true"
        className={`flex size-4 items-center justify-center rounded-full text-[10px] font-semibold ${mark.className}`}
      >
        {marketplace === 'other' ? <StoreIcon className="size-2.5" aria-hidden /> : mark.short}
      </span>
      {label}
    </Badge>
  )
}
